import { DateTime } from "luxon";
import config from "../config";
import { SeatStatus } from "../models/AssignedSeating";
import { EcommercePayment } from "../requests/OrderRequests";
import AuthStore from "../stores/AuthStore";
import ThemeStore from "../stores/ThemeStore";

export const IsMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        typeof navigator !== "undefined" ? navigator.userAgent : "",
    );
};

export const IsAppleDevice = () => {
    return (
        (typeof navigator !== "undefined" &&
            navigator.userAgent.match(/(iPhone|iPad|iPod|Safari)/) &&
            window.innerWidth <= 1024) ||
        (typeof navigator !== "undefined" && navigator.platform === "MacIntel")
    );
};

export const getImageUrl = (image: string) => {
    if (image.startsWith("http")) return image;
    return `${config.UPLOAD_URI}/${image}`;
};

export const displayDateRange = (
    fromDate: number,
    toDate: number,
    showWeekday: boolean = false,
) => {
    const from = DateTime.fromMillis(fromDate);
    const to = DateTime.fromMillis(toDate);
    if (from.hasSame(to, "day")) {
        return showWeekday
            ? `${from.toFormat("cccc LLLL d, yyyy")}`
            : `${from.toFormat("LLLL d, yyyy")}`;
    }
    if (from.hasSame(to, "month")) {
        return showWeekday
            ? `${from.toFormat("cccc LLLL d")} - ${to.toFormat(
                  "cccc LLLL d, yyyy",
              )}`
            : `${from.toFormat("LLLL d")} - ${to.toFormat("d, yyyy")}`;
    }
    if (from.hasSame(to, "year")) {
        return showWeekday
            ? `${from.toFormat("cccc LLLL d")} - ${to.toFormat(
                  "cccc LLLL d, yyyy",
              )}`
            : `${from.toFormat("LLLL d")} - ${to.toFormat("LLLL d, yyyy")}`;
    }
    return showWeekday
        ? `${from.toFormat("cccc LLLL d, yyyy")} - ${to.toFormat(
              "cccc LLLL d, yyyy",
          )}`
        : `${from.toFormat("LLLL d, yyyy")} - ${to.toFormat("LLLL d, yyyy")}`;
};

export const getPriceRange = (
    prices: Array<number>,
    hasFees: boolean = false,
    hasTaxes: boolean = false,
) => {
    if (prices.length === 0) return "";
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    if (min === max)
        return `$${min.toFixed(2)}${hasFees ? " + fees" : ""}${
            hasTaxes ? " + taxes" : ""
        }`;
    const minString = min === 0 ? "Free" : `$${min.toFixed(2)}`;
    return `${minString} - $${max.toFixed(2)}${hasFees ? " + fees" : ""}${
        hasTaxes ? " + taxes" : ""
    }`;
};

export const clamp = (value: number, min: number, max: number) => {
    return Math.min(Math.max(value, min), max);
};

export const getSeatFillColor = (status: SeatStatus, color: string) => {
    if (status === SeatStatus.RESERVED && AuthStore.loggedIn) {
        return "#242424";
    }
    if (status === SeatStatus.UNAVAILABLE || status === SeatStatus.RESERVED) {
        return "#575757";
    }
    return color;
};

export const getRandomValues = (buf: any): any => {
    const win = window as any;
    if (win.crypto && win.crypto.getRandomValues) {
        return win.crypto.getRandomValues(buf);
    }
    if (
        typeof win.msCrypto === "object" &&
        typeof win.msCrypto.getRandomValues === "function"
    ) {
        return win.msCrypto.getRandomValues(buf);
    } else {
        throw new Error("No secure random number generator available.");
    }
};

export const generateRandomString = (length: number): string => {
    const splitAt = 7;
    const array = new Uint8Array(length);
    getRandomValues(array);

    const longNumberString = array.reduce((a, b) => "" + a + ("" + b), "");
    let result = "";
    for (let i = 0; i < Math.floor(longNumberString.length / splitAt); i++) {
        const substr = longNumberString.substr(i * splitAt, splitAt);
        result += "" + parseInt(substr).toString(36);
    }
    return result;
};

export const generateCartKey = (): string => {
    return generateRandomString(40);
};

export const priceFormatter = (price: number): string => {
    const dollars = price / 100;
    return "$" + dollars.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// The department price for display: listings always show the pre-tax amount. The
// server computes this as basePrice (price as-is when exclusive, included tax backed
// out when inclusive); older backends without it fall back to subtracting the taxes
// breakdown locally, or to the advertised price when neither is available.
export const departmentPriceLessTax = (dept: {
    price: number;
    basePrice?: number;
    taxInclusive?: boolean;
    taxes?: Array<{ amount: number }>;
}): number => {
    if (dept.basePrice !== null && dept.basePrice !== undefined) {
        return dept.basePrice;
    }
    return dept.taxInclusive && dept.taxes
        ? dept.price - dept.taxes.reduce((a, t) => a + t.amount, 0)
        : dept.price;
};

export const numberFormatter = (num: number): string => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const setupStyleMapFromStyleMap = (styles: any) => {
    ThemeStore.setStyles(styles);
};

export const getErrorMessage = (err: any, fallback?: string): string => {
    return (
        err.response?.data ??
        err.message ??
        err ??
        fallback ??
        "An error occurred."
    );
};

export const getErrorWithData = (
    err: any,
): {
    message: string;
    errorCode: string;
    data: {
        [key: string]: any;
    };
} => {
    return {
        ...err,
    };
};

export const IFrameDecorder = (value: string): EcommercePayment | null => {
    try {
        // Extract JSON out of "iframe-JSON---"
        const text = Buffer.from(value, "base64").toString("ascii");
        const decoded = JSON.parse(text.substring(7, text.length - 3));
        if (!decoded || decoded === "") {
            return null;
        }

        return {
            discriminator: "ECOMMERCE",
            cardNum: decoded.cardNum || "",
            nameOnCard: decoded.nameOnCard || "",
            cvv: decoded.cvv || "",
            expiryMonth: decoded.expiryMonth || "",
            expiryYear: decoded.expiryYear || "",
        };
    } catch (err) {
        console.warn("Error while decoding CC data from IFrame", err);
        return null;
    }
};

export function isTouchEnabled() {
    return "ontouchstart" in window || navigator.maxTouchPoints > 0;
}

export function newShade(hexColor: string, magnitude: number): string {
    hexColor = hexColor.replace(`#`, ``);
    if (hexColor.length === 6) {
        const decimalColor = parseInt(hexColor, 16);
        let r = (decimalColor >> 16) + magnitude;
        r > 255 && (r = 255);
        r < 0 && (r = 0);
        let g = (decimalColor & 0x0000ff) + magnitude;
        g > 255 && (g = 255);
        g < 0 && (g = 0);
        let b = ((decimalColor >> 8) & 0x00ff) + magnitude;
        b > 255 && (b = 255);
        b < 0 && (b = 0);
        return `#${(g | (b << 8) | (r << 16)).toString(16)}`;
    } else {
        return hexColor;
    }
}

export function capitalizeWords(str: string) {
    return str.replace(/\w\S*/g, function (txt) {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
}

export function calculateAngle(
    center: { x: number; y: number },
    point: { x: number; y: number },
): number {
    const angle = Math.atan2(point.y - center.y, point.x - center.x);
    return angle;
}

export const FileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        let data: string | ArrayBuffer | null = "";
        reader.addEventListener(
            "load",
            () => {
                data = (reader.result as string).split(",")[1];
                resolve(data);
            },
            false,
        );
        reader.readAsDataURL(file);
    });
};
